import fleetlogicAPI from "helpers/fleetlogicAPI";
import i18n from "i18n/i18n";
import Uri from "jsuri";
import isEmpty from "lodash/isEmpty";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Button, Checkbox, Grid, Icons, Modal, Select } from "@texada/ui";
import { StatusCodes } from "http-status-codes";

import { addMessage, logout } from "actions";
import { HEADOFFICE_OPTION_VALUE, SCS_SRM_UID } from "constants/AppConstants";
import {
  filterSortMapOptions,
  getApiErrorMsg,
  ReduxState,
  SrmBoolean,
  WINDOW_EVENT
} from "helpers/helper";
import useToggle from "helpers/hooks";
import { toDivisionTime } from "helpers/timeHelpers";

import { ContractArchiveModal } from "./ContractArchiveModal";
import QuickbarHeading from "./QuickbarHeading";
import UserInstance from "./UserInstance";

import "./Quickbar.scss";

const CLOCK_IN_VALUE = "ClockInValue";

function to2Digits(num: number) {
  return num.toLocaleString("en-US", {
    minimumIntegerDigits: 2,
    useGrouping: false
  });
}

function formatDuration(startTime: moment.Moment) {
  if (startTime === null) {
    return null;
  }

  let differ = moment().unix() - startTime.unix();
  const seconds = differ % 60;
  // trim seconds
  differ -= seconds;
  // convert to minutes
  differ /= 60;
  const minutes = differ % 60;
  differ -= minutes;
  const hours = differ / 60;

  return `${to2Digits(hours)}:${to2Digits(minutes)}:${to2Digits(seconds)}`;
}

export function usePostClock() {
  const user = useSelector<
    ReduxState,
    { id: number; name: string; srm_uid: string }
  >(state => state.auth.user);
  return async () => {
    const start = toDivisionTime(
      moment(localStorage.ClockInValue).format(),
      ""
    );
    const end = toDivisionTime(moment().format(), "");

    await fleetlogicAPI
      .post("/rest-api/clock/", {
        user_auth_id: user.id,
        user_name: user.name,
        user_srm_uid: user.srm_uid,
        timestamp: start,
        record_type: "1"
      })
      .then(async () => {
        await fleetlogicAPI
          .post("/rest-api/clock/", {
            user_auth_id: user.id,
            user_name: user.name,
            user_srm_uid: user.srm_uid,
            timestamp: end,
            record_type: "0"
          })
          .then(() => {
            localStorage.removeItem(CLOCK_IN_VALUE);
          });
      });
  };
}

function useOnClockChange(onChange: (clock: string) => void) {
  const prevClock = useRef<string>();
  useEffect(() => {
    prevClock.current = localStorage.getItem(CLOCK_IN_VALUE);

    function onStorage() {
      const clock = localStorage.getItem(CLOCK_IN_VALUE);
      if (clock !== prevClock.current) {
        prevClock.current = clock;
        onChange(clock);
      }
    }

    window.addEventListener(WINDOW_EVENT.STORAGE, onStorage);
    return () => {
      window.removeEventListener(WINDOW_EVENT.STORAGE, onStorage);
    };
  }, []);
}

export function useDuration() {
  const postClock = usePostClock();

  const [duration, setDuration] = useState(() => {
    const clockInValue = localStorage.getItem(CLOCK_IN_VALUE);
    if (clockInValue !== null) {
      const storedClockIn = moment(clockInValue);
      return storedClockIn;
    }
    return null;
  });
  const [message, setMessage] = useState(null);

  useOnClockChange(time => {
    setDuration(time === null ? null : moment(time));
  });

  return {
    duration,
    message,
    clockIn() {
      const time = moment();
      localStorage.setItem(CLOCK_IN_VALUE, time.toString());
      setDuration(time);
    },
    async clockOut() {
      try {
        await postClock();
        setDuration(null);

        setMessage({
          success: true,
          info: i18n.t("time_saved")
        });
        window.setTimeout(() => {
          setMessage(null);
        }, 3000);
      } catch {
        setMessage({
          success: false,
          info: i18n.t("failed")
        });
        window.setTimeout(() => {
          setMessage(null);
        }, 3000);
      }
    }
  };
}

function UserClock({
  duration,
  message,
  clockIn,
  clockOut
}: ReturnType<typeof useDuration>) {
  const [elapsedTime, setElapsedTime] = useState(() =>
    formatDuration(duration)
  );

  useEffect(() => {
    if (duration === null) return;

    function refreshDuration() {
      setElapsedTime(formatDuration(duration));
    }
    const interval = window.setInterval(refreshDuration, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [duration]);

  return (
    <div className="quickbar-user-clock">
      <Button
        className={`quickbar-button clocked-${duration ? "in" : "out"}`}
        icon={{
          type: "left",
          render: duration ? Icons.Stop : Icons.Next
        }}
        onClick={() => {
          duration ? clockOut() : clockIn();
        }}
      >
        {duration ? i18n.t("clock_out") : i18n.t("clock_in")}
      </Button>

      <span
        className="quickbar-clocked-message"
        data-testid="quickbar-clocked-message"
      >
        {message !== null && message.info}
        {elapsedTime}
      </span>
    </div>
  );
}

const QuickbarUser: React.FC<{
  isOpen: boolean;
  onCancel: () => void;
  setIsQuickbarOpen: (isOpen: string) => void;
}> = props => {
  const user = useSelector<ReduxState, any>((state: any) => state.auth.user);

  const postClock = usePostClock();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSRM, setLoadingSRM] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [selectedDivision, setSelectedDivision] = useState();
  const [isDivisionModalOpen, setIsDivisionModalOpen] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [divisionError, setDivisionError] = useState("");
  const { duration, message, clockIn, clockOut } = useDuration();
  const { showContractArchive, openContractArchive, closeContractArchive } =
    useToggle({ name: "contractArchive" });
  const hasArchiveContractPermission =
    (user?.permissions?.admin && user.upgraded) || user?.contract_archive;
  const [searchDivision, setSearchDivision] = useState("");
  const isScsUser = user?.srm_uid === SCS_SRM_UID;

  useEffect(() => {
    if (!props.isOpen) return;
    const defaultDivision = localStorage.getItem("defaultDivision");
    if (defaultDivision) {
      const target = user.login_divisions.find(
        e => e?.warehouse?.srm_uid === defaultDivision.trim()
      );
      if (target) {
        setSelectedDivision({
          ...target,
          value: target?.DivLocId || target?.warehouse?.srm_uid
        });
        setSaveAsDefault(true);
        return;
      }
    }
  }, [props.isOpen]);

  const openSrmManagement = async division => {
    setDivisionError("");
    setLoadingSRM(true);
    const url = new Uri("/rest-api/srmlogin/");
    if (division) {
      if (!isScsUser && division.DivLocId) {
        url.addQueryParam("div_loc_id", division.DivLocId);
      } else {
        url.addQueryParam("division", division.warehouse.srm_uid.trim());
      }
      if (saveAsDefault) {
        localStorage.setItem("defaultDivision", division.warehouse.srm_uid);
      } else {
        localStorage.removeItem("defaultDivision");
      }
    }

    try {
      const loginURL = await fleetlogicAPI.get(url.toString());

      window.open(loginURL.data, "_blank");
      setLoadingSRM(false);
      setSelectedDivision(null);
      setIsDivisionModalOpen(false);
      props.setIsQuickbarOpen("false");
    } catch (error) {
      if (error.response?.status === StatusCodes.NOT_FOUND) {
        setDivisionError(getApiErrorMsg(error));
      } else {
        dispatch(
          addMessage({
            type: "error",
            text: i18n.t("could_not_login_to_srf_classic")
          })
        );
      }
      setLoadingSRM(false);
    }
  };

  useEffect(() => {
    if (user.upgraded) {
      setCompanyName(user?.userinstance?.current?.name);
      return;
    }
    setCompanyName(user.company_title);
  }, [user]);

  function switchToSrm() {
    if (!user?.compperms?.operator_by_division && (isScsUser || user?.login_divisions?.length <= 1)) {
      openSrmManagement();
      return;
    }

    if (!user?.compperms?.operator_by_division && localStorage.getItem("selectedDivisionSrmUid")) {
      const target = user.login_divisions.find(
        e => (isScsUser ? e?.warehouse?.srm_uid : e?.DivLocId) === localStorage.getItem("selectedDivisionSrmUid").trim()
      );
      if (target){
        openSrmManagement(target);
        return;
      }
    }
    let defaultDivision = (user?.login_divisions || []).find(e => e.is_default);
    if (isEmpty(defaultDivision)) {
      props.setIsQuickbarOpen("false");
      setIsDivisionModalOpen(true);
      return;
    }

    openSrmManagement(defaultDivision);
  }

  const divisionList = isScsUser
    ? (user?.upgraded
        ? (user?.divisions || []).filter(e => e.is_user_division)
        : (user?.divisions || []))
    : (user?.login_divisions || []);
  const divisionOptions = filterSortMapOptions(
    divisionList
      ?.filter(warehouse => (user.upgraded && isScsUser ? warehouse.is_user_division : true))
      .map(item => {
        const baseWarehouse = item?.warehouse || {};
        const loginWarehouse = item?.login_warehouse || baseWarehouse;

        return {
          ...baseWarehouse,
          srm_uid: loginWarehouse?.srm_uid || baseWarehouse?.srm_uid,
          title: loginWarehouse?.title || baseWarehouse?.title,
          value: isScsUser ? baseWarehouse?.srm_uid : item?.DivLocId || HEADOFFICE_OPTION_VALUE,
          alternate_division_number: loginWarehouse?.alternate_division_number,
          operator_code: item?.DivLocId?.split(':')?.[2]?.trim() || '',
          label: `[${
            user?.systemInfo?.display_alternate_division_number === SrmBoolean.True &&
            (loginWarehouse?.alternate_division_number || baseWarehouse?.alternate_division_number)
              ? (loginWarehouse?.alternate_division_number || "\xa0".repeat(4))
              : (loginWarehouse?.srm_uid || "\xa0".repeat(4))
          }${
            !isScsUser && item?.DivLocId?.split(':')?.[2]?.trim()
              ? ` - ${item?.DivLocId?.split(':')?.[2]?.trim()}`
              : ""
          }] — ${baseWarehouse?.title}`,
        };
      }),
    searchDivision,
    ["srm_uid", "operator_code", "title", "label", "alternate_division_number"],
    "label",
    true,
    warehouse => ({
      ...warehouse,
      label: warehouse.label,
      value: warehouse.value || HEADOFFICE_OPTION_VALUE
    })
  );

  return (
    <>
      <Modal
        {...props}
        header={i18n.t("user_menu")}
        size="quickbar"
        shouldReturnFocusAfterClose={false}
        closeButton
        isLoading={isLoading}
      >
        <Modal.Body>
          <div className="quickbar-user-top">
            <QuickbarHeading
              size="default"
              className={duration ? "clocked-in" : "clocked-out"}
              heading={user.name}
              subHeading={
                duration ? i18n.t("clocked_in") : i18n.t("clocked_out")
              }
              icon={{ render: Icons.Face }}
            />
            <QuickbarHeading
              className="quickbar-company"
              size="default"
              heading={companyName}
              icon={{ render: Icons.Business }}
            />
          </div>
          <UserClock
            duration={duration}
            message={message}
            clockIn={clockIn}
            clockOut={clockOut}
          />

          {user?.permissions?.admin && (
            <Button
              as={Link}
              buttonProps={{ to: "/setting" }}
              className="quickbar-button"
              icon={{ type: "left", render: Icons.Settings }}
              onClick={() => {
                props.setIsQuickbarOpen("false");
              }}
            >
              {i18n.t("settings")}
            </Button>
          )}

          <Button
            className="quickbar-button"
            icon={{ type: "left", render: Icons.Dvr }}
            onClick={switchToSrm}
            isLoading={loadingSRM}
          >
            {i18n.t("switch_to_classic")}
          </Button>

          {user?.companyFeatures?.contract_archive &&
          hasArchiveContractPermission ? (
            <Button
              className="quickbar-button"
              icon={{ type: "left", render: Icons.Archive }}
              onClick={() => {
                openContractArchive();
                props.setIsQuickbarOpen("false");
              }}
            >
              {i18n.t("contract_archive")}
            </Button>
          ) : (
            <></>
          )}
          <UserInstance
            setIsLoading={setIsLoading}
            duration={duration}
            clockOut={clockOut}
          />

          <Button
            className="quickbar-button"
            icon={{ type: "left", render: Icons.Exit }}
            onClick={async () => {
              if (duration === null) {
                dispatch(logout());
              } else {
                await postClock().finally(() => {
                  dispatch(logout());
                });
              }
            }}
          >
            {i18n.t("log_out")}
          </Button>
        </Modal.Body>
      </Modal>
      <Modal
        isOpen={isDivisionModalOpen}
        header={i18n.t("select_division")}
        onCancel={() => {
          setIsDivisionModalOpen(false);
          setSelectedDivision(null);
          setSearchDivision("");
          setDivisionError("");
        }}
        isLoading={loadingSRM}
      >
        <Modal.Body padded={false}>
          <Grid.Row>
            <Grid.Col>
              <Select
                label={i18n.t("division")}
                required
                placeholder={i18n.t("select_division")}
                value={selectedDivision?.value}
                options={divisionOptions}
                shouldFilter={false}
                onInputChange={(value: string) => setSearchDivision(value)}
                onChange={selected => {
                  const selectedValue = (selected.target.value || "").trim();
                  const target = divisionList.find(e => {
                    if (isScsUser) {
                      return (e?.warehouse?.srm_uid || e?.srm_uid) === selectedValue;
                    } else {
                      return (e?.DivLocId || e?.warehouse?.srm_uid) === selectedValue;
                    }
                  });
                  setSelectedDivision(
                    target
                      ? {
                          ...target,
                          value: selected.target.value
                        }
                      : null
                  );
                }}
                error={divisionError}
                clearable={true}
              />
            </Grid.Col>
          </Grid.Row>
          <Grid.Row>
            <Grid.Col>
              <Checkbox
                name="checkbox"
                label={i18n.t("set_as_default_division_for_future_logins")}
                checked={saveAsDefault}
                onChange={e => {
                  setSaveAsDefault(e.target.value);
                }}
              />
            </Grid.Col>
          </Grid.Row>
        </Modal.Body>
        <Modal.Footer
          saveButton
          saveButtonText={i18n.t("switch_to_classic")}
          closeButton
          onSubmit={() => {
            if (isEmpty(selectedDivision)) {
              setDivisionError(
                i18n.t("field_is_required", { field: i18n.t("division") })
              );
              return;
            }
            openSrmManagement(selectedDivision);
          }}
        />
      </Modal>
      <ContractArchiveModal
        isOpen={showContractArchive}
        onCancel={closeContractArchive}
      />
    </>
  );
};

export default QuickbarUser;
